import {
  IQuoteRespSchema,
  QuoteOfferingGroup,
  QuoteOfferingGroups,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { useQueryClient } from '@tanstack/react-query';
import uniq from 'lodash/uniq';
import { useCallback, useEffect, useRef } from 'react';
import { handleApiErrorToast } from '../api/axios';
import {
  cpqServiceQueryKeys,
  useDeleteQuoteOfferingGroup,
  useUpdateQuoteOfferingGroup,
} from '../api/cpqService';
import { buildQuoteOfferingGroupMembershipRequest } from '../utils/quotes';

/**
 * Quote offering group membership + lifecycle actions for the quote page.
 * All mutations are pessimistic — the groups query is invalidated on success
 * and the offering table re-partitions from the refetched groups.
 */
export const useQuoteOfferingGroupActions = (
  quote: Maybe<IQuoteRespSchema>,
) => {
  const quoteId = quote?.id || '';
  const quoteOfferings = quote?.quoteOfferings;
  const queryClient = useQueryClient();
  const { mutateAsync: doUpdateGroup, isPending: isUpdatePending } =
    useUpdateQuoteOfferingGroup(quoteId);
  const { mutateAsync: doDeleteGroup, isPending: isDeletePending } =
    useDeleteQuoteOfferingGroup(quoteId);

  /**
   * Offerings added to a group that `quote` has not caught up to yet.
   *
   * Bulk add creates its offerings one at a time and re-fetches the quote once
   * at the very end, so for the whole run `quoteOfferings` predates every
   * offering the run created. The membership payload sanitizes the group's
   * stored ids against that list (an id the quote no longer has would fail the
   * PUT), which drops each previously added offering — the group ends up
   * holding only the last one. Ids sent as *additions* skip the sanitizing, so
   * re-sending them keeps the group whole.
   *
   * Cleared as soon as a fresh quote lands: from then on the quote knows about
   * these offerings, and anything still missing from it has been deleted and
   * must not be written back.
   */
  const pendingAddedIdsRef = useRef<string[]>([]);
  useEffect(() => {
    pendingAddedIdsRef.current = [];
  }, [quoteOfferings]);

  const addOfferingsToGroup = useCallback(
    async (group: QuoteOfferingGroup, quoteOfferingIds: string[]) => {
      /**
       * A group PUT declares the group's complete membership, so it has to be
       * built from the group's *current* members. The caller's `group` is a
       * render-time snapshot: two adds in quick succession (bulk add, or a fast
       * second pick) would both build from the pre-first-add membership, and the
       * second write would evict the first offering from the group. The cache is
       * patched optimistically on every group write, so it is current here even
       * before the previous response lands.
       */
      const cachedGroup = queryClient
        .getQueryData<QuoteOfferingGroups>([
          ...cpqServiceQueryKeys.quoteOfferingGroups(quoteId),
        ])
        ?.groups?.find(({ id }) => id === group.id);

      try {
        await doUpdateGroup({
          quoteOfferingGroupId: group.id,
          payload: buildQuoteOfferingGroupMembershipRequest({
            group: cachedGroup || group,
            quoteOfferings,
            addQuoteOfferingIds: [
              ...pendingAddedIdsRef.current,
              ...quoteOfferingIds,
            ],
          }),
        });
        pendingAddedIdsRef.current = uniq([
          ...pendingAddedIdsRef.current,
          ...quoteOfferingIds,
        ]);
        return true;
      } catch (error) {
        handleApiErrorToast(error);
        return false;
      }
    },
    [doUpdateGroup, quoteOfferings, queryClient, quoteId],
  );

  /** Deletes the container only — offerings stay on the quote. */
  const deleteGroup = useCallback(
    async (group: QuoteOfferingGroup) => {
      try {
        await doDeleteGroup(group.id);
        return true;
      } catch (error) {
        handleApiErrorToast(error);
        return false;
      }
    },
    [doDeleteGroup],
  );

  return {
    addOfferingsToGroup,
    deleteGroup,
    isPending: isUpdatePending || isDeletePending,
  };
};
